import { createSlice } from "@reduxjs/toolkit";

export type ChannelUserMode = "Multi-user" | "Single user";

export type ChannelStatus =
  | "Draft"
  | "Submitted"
  | "Under review"
  | "Sent back"
  | "Approved"
  | "Active"
  | "Suspended"
  | "Rejected";

export type ChannelFilterOption = {
  value: string;
  label: string;
};

export type Channel = {
  id: number;
  channelCode: string;
  name: string;
  type: string;
  userMode: ChannelUserMode;
  status: ChannelStatus;
  users: number;
  licenceValidity: string;
  licenceNote?: string;
  lastUpdated: string;
};

export type ChannelList = {
  total: number;
  page: number;
  page_size: number;
  data: Channel[];
};

export type ChannelState = {
  channelList: ChannelList;
  channelTypeOptions: ChannelFilterOption[];
  statusOptions: ChannelFilterOption[];
};

// Static filter/type option lists. Swap per-option values with API-driven
// lists later without touching the consuming page.
const channelTypeOptions: ChannelFilterOption[] = [
  { value: "Bancassurance", label: "Bancassurance" },
  { value: "Common Service Centre (CSC)", label: "Common Service Centre (CSC)" },
  { value: "Corporate Agent (CA)", label: "Corporate Agent (CA)" },
  { value: "Direct Sales Agent (DSA)", label: "Direct Sales Agent (DSA)" },
  { value: "Individual Agent", label: "Individual Agent" },
  { value: "Insurance Broker", label: "Insurance Broker" },
  { value: "Insurance Marketing Firm (IMF)", label: "Insurance Marketing Firm (IMF)" },
  { value: "Point of Sales Person (PoSP)", label: "Point of Sales Person (PoSP)" },
  { value: "Web Aggregator / Digital Platform", label: "Web Aggregator / Digital Platform" },
];

const statusOptions: ChannelFilterOption[] = [
  { value: "Draft", label: "Draft" },
  { value: "Submitted", label: "Submitted" },
  { value: "Under review", label: "Under review" },
  { value: "Sent back", label: "Sent back" },
  { value: "Approved", label: "Approved" },
  { value: "Active", label: "Active" },
  { value: "Suspended", label: "Suspended" },
  { value: "Rejected", label: "Rejected" },
];

// Static demo data shown on the Channels page until the API is wired up.
const initialState: ChannelState = {
  channelTypeOptions,
  statusOptions,
  channelList: {
    total: 18,
    page: 1,
    page_size: 10,
    data: [
      {
        id: 1,
        channelCode: "BRK-1001",
        name: "Sterling Insurance Brokers Pvt Ltd",
        type: "Insurance Broker",
        userMode: "Multi-user",
        status: "Active",
        users: 3,
        licenceValidity: "31 Mar 2027",
        lastUpdated: "26 Aug 2026",
      },
      {
        id: 2,
        channelCode: "CAG-2004",
        name: "Vertex Retail Corporate Agency",
        type: "Corporate Agent (CA)",
        userMode: "Multi-user",
        status: "Under review",
        users: 0,
        licenceValidity: "30 Nov 2026",
        lastUpdated: "26 Aug 2026",
      },
      {
        id: 3,
        channelCode: "BNC-3007",
        name: "Meridian Bank Ltd",
        type: "Bancassurance",
        userMode: "Multi-user",
        status: "Active",
        users: 3,
        licenceValidity: "15 Sept 2026",
        licenceNote: "Expires in 17 days",
        lastUpdated: "26 Aug 2026",
      },
      {
        id: 4,
        channelCode: "IMF-4102",
        name: "Kaveri Insurance Marketing Firm",
        type: "Insurance Marketing Firm (IMF)",
        userMode: "Multi-user",
        status: "Suspended",
        users: 0,
        licenceValidity: "20 Aug 2026",
        licenceNote: "Expired",
        lastUpdated: "26 Aug 2026",
      },
      {
        id: 5,
        channelCode: "WEB-5210",
        name: "PolicyNexa Digital Pvt Ltd",
        type: "Web Aggregator / Digital Platform",
        userMode: "Multi-user",
        status: "Active",
        users: 3,
        licenceValidity: "31 Jan 2028",
        lastUpdated: "26 Aug 2026",
      },
      {
        id: 6,
        channelCode: "AGT-6014",
        name: "Ramesh Iyer",
        type: "Individual Agent",
        userMode: "Single user",
        status: "Active",
        users: 3,
        licenceValidity: "05 Sept 2026",
        licenceNote: "Expires in 7 days",
        lastUpdated: "26 Aug 2026",
      },
      {
        id: 7,
        channelCode: "PSP-7031",
        name: "Neha Bansal",
        type: "Point of Sales Person (PoSP)",
        userMode: "Single user",
        status: "Sent back",
        users: 0,
        licenceValidity: "30 Jun 2027",
        lastUpdated: "26 Aug 2026",
      },
      {
        id: 8,
        channelCode: "CSC-8003",
        name: "CSC e-Governance Rural Network",
        type: "Common Service Centre (CSC)",
        userMode: "Multi-user",
        status: "Approved",
        users: 3,
        licenceValidity: "31 Dec 2027",
        lastUpdated: "26 Aug 2026",
      },
      {
        id: 9,
        channelCode: "BRK-1009",
        name: "Anand Insurance Broking House",
        type: "Insurance Broker",
        userMode: "Multi-user",
        status: "Active",
        users: 2,
        licenceValidity: "18 Feb 2027",
        lastUpdated: "25 Aug 2026",
      },
      {
        id: 10,
        channelCode: "CAG-2011",
        name: "Blue Horizon Corporate Agency",
        type: "Corporate Agent (CA)",
        userMode: "Multi-user",
        status: "Under review",
        users: 0,
        licenceValidity: "12 Dec 2026",
        lastUpdated: "24 Aug 2026",
      },
      {
        id: 11,
        channelCode: "AGT-6022",
        name: "Priya Menon",
        type: "Individual Agent",
        userMode: "Single user",
        status: "Approved",
        users: 1,
        licenceValidity: "09 Oct 2027",
        lastUpdated: "24 Aug 2026",
      },
      {
        id: 12,
        channelCode: "DSA-3315",
        name: "Horizon Direct Sales Associates",
        type: "Direct Sales Agent (DSA)",
        userMode: "Multi-user",
        status: "Draft",
        users: 0,
        licenceValidity: "—",
        lastUpdated: "23 Aug 2026",
      },
      {
        id: 13,
        channelCode: "DSA-3322",
        name: "Karan Chopra",
        type: "Direct Sales Agent (DSA)",
        userMode: "Single user",
        status: "Submitted",
        users: 0,
        licenceValidity: "—",
        lastUpdated: "23 Aug 2026",
      },
      {
        id: 14,
        channelCode: "BNC-3014",
        name: "Coastal Cooperative Bank",
        type: "Bancassurance",
        userMode: "Multi-user",
        status: "Rejected",
        users: 0,
        licenceValidity: "10 May 2026",
        licenceNote: "Expired",
        lastUpdated: "22 Aug 2026",
      },
      {
        id: 15,
        channelCode: "IMF-4118",
        name: "Zenith Insurance Marketing",
        type: "Insurance Marketing Firm (IMF)",
        userMode: "Multi-user",
        status: "Active",
        users: 4,
        licenceValidity: "22 Jul 2027",
        lastUpdated: "22 Aug 2026",
      },
      {
        id: 16,
        channelCode: "CSC-8017",
        name: "Grameen Digital Seva Kendra",
        type: "Common Service Centre (CSC)",
        userMode: "Multi-user",
        status: "Under review",
        users: 0,
        licenceValidity: "14 Apr 2027",
        lastUpdated: "21 Aug 2026",
      },
      {
        id: 17,
        channelCode: "PSP-7045",
        name: "Farhan Sheikh",
        type: "Point of Sales Person (PoSP)",
        userMode: "Single user",
        status: "Suspended",
        users: 0,
        licenceValidity: "01 Jan 2026",
        licenceNote: "Expired",
        lastUpdated: "20 Aug 2026",
      },
      {
        id: 18,
        channelCode: "WEB-5233",
        name: "Quantum Insure Web Pvt Ltd",
        type: "Web Aggregator / Digital Platform",
        userMode: "Multi-user",
        status: "Sent back",
        users: 2,
        licenceValidity: "19 Sept 2027",
        lastUpdated: "20 Aug 2026",
      },
    ],
  },
};

const channelSlice = createSlice({
  name: "channel",
  initialState,
  reducers: {
    setChannelList: (state, action: { payload: ChannelList }) => {
      state.channelList = action.payload;
    },
    setChannelTypeOptions: (state, action: { payload: ChannelFilterOption[] }) => {
      state.channelTypeOptions = action.payload;
    },
    setChannelStatusOptions: (state, action: { payload: ChannelFilterOption[] }) => {
      state.statusOptions = action.payload;
    },
  },
});

export const { setChannelList, setChannelTypeOptions, setChannelStatusOptions } =
  channelSlice.actions;
export default channelSlice.reducer;
